import React, { memo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';

import { convertReliabilityScoreToDisplayValue } from '@careerstart/wae-common/src/main/helperFunction/convertReliabilityScoreToDisplayValue';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import DoDisturbAltIcon from '@mui/icons-material/DoDisturbAlt';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { Avatar, Box, Card, CardContent } from '@mui/material';
import Typography from '@mui/material/Typography';

import { BLACK, FEED_BACK, PRIMARY_PURPLE } from '../../theme/colorConstants';
import useFairReliabilityScore from '../../utils/useFairReliabilityScore';
import stringAvatar from '../CandidatPickerCard/SubComponent/stringAvatar';
import WorkerNameLink from '../WorkerNameLink/WorkerNameLink';

import { formatDisplayPhoneNumber } from './recommendWorkerHelper';
import WorkerActionButtons from './WorkerActionButtons';

const RecommendWorkerCard = memo(({ candidate, user, jobOrderId, onSuccess }) => {
  const flags = useFlags();
  const { pickReliability } = useFairReliabilityScore();
  const displayedReliabilityScore = pickReliability(
    candidate?.rawReliability,
    candidate?.reliabilityScore
  );
  const isOverridePlacementCreationRulesButtonEnabled =
    flags?.isWebAdminOverridePlacementCreationRulesButtonEnabled ?? false;

  const canShowOverrideButton =
    candidate?.ineligibleReason && isOverridePlacementCreationRulesButtonEnabled;

  return (
    <Card
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        backgroundColor: candidate?.ineligibleReason ? BLACK[10] : BLACK[0],
        width: '100%',
        minWidth: 0,
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: { xs: 'stretch', lg: 'center' },
          gap: { xs: 1.5, lg: 1 },
          minWidth: 0,
          '&:last-child': { pb: 2 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            flexGrow: 1,
            minWidth: 0,
            width: { xs: '100%', lg: 'auto' },
            gap: { xs: 1.5, lg: 0 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            {candidate?.imageUrl ? (
              <Avatar alt={`${candidate.name}`} src={`${candidate.imageUrl}`} />
            ) : (
              <Avatar
                {...stringAvatar(candidate)}
                sx={{
                  marginTop: '-8px',
                  flexShrink: 0,
                }}
              />
            )}
            <Box sx={{ flexGrow: 1, marginLeft: '10px', minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
              >
                <FavoriteIcon fontSize="small" sx={{ color: PRIMARY_PURPLE[70], mr: 1 }} />
                <WorkerNameLink
                  name={candidate?.name}
                  userId={candidate?._id}
                  agencyId={candidate?.agencyId}
                />
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {candidate?.email}
              </Typography>
              <Typography variant="body2">
                {LanguageConverterV2('dashboard.recommendWorkerModal.phoneNumber')}
                {formatDisplayPhoneNumber(candidate?.phoneNumber)}
              </Typography>
            </Box>
          </Box>

          <Box
            textAlign="right"
            sx={{
              minWidth: { xs: 110, lg: 120 },
              flexShrink: 0,
              pl: { xs: 0, lg: 1 },
            }}
          >
            <Typography variant="body2" data-testid="recommend-worker-reliability-score">
              {LanguageConverterV2('dashboard.recommendWorkerModal.reliabilityScore')}
              <strong style={{ color: FEED_BACK.GREEN_70 }}>
                {convertReliabilityScoreToDisplayValue(displayedReliabilityScore)}
              </strong>
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'stretch', lg: 'center' },
            paddingLeft: { xs: 0, lg: '16px' },
            width: { xs: '100%', lg: 'auto' },
            flexShrink: 0,
          }}
        >
          {candidate?.ineligibleReason ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              <DoDisturbAltIcon
                sx={{
                  color: FEED_BACK.RED_50,
                  fontSize: '2rem',
                }}
              />
              <Typography
                sx={{
                  color: FEED_BACK.RED_50,
                  fontSize: '14px',
                  textAlign: 'center',
                  maxWidth: { xs: '100%', lg: '140px' },
                  wordBreak: 'break-word',
                }}
              >
                {LanguageConverterV2(candidate?.ineligibleReason)}
              </Typography>
              {canShowOverrideButton && (
                <WorkerActionButtons
                  candidate={candidate}
                  user={user}
                  jobOrderId={jobOrderId}
                  onSuccess={onSuccess}
                  isOverrideMode
                />
              )}
            </Box>
          ) : (
            <WorkerActionButtons
              candidate={candidate}
              user={user}
              jobOrderId={jobOrderId}
              onSuccess={onSuccess}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
});

RecommendWorkerCard.propTypes = {
  candidate: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    agencyId: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    imageUrl: PropTypes.string,
    reliabilityScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rawReliability: PropTypes.number,
    ineligibleReason: PropTypes.string,
  }),
  user: PropTypes.shape({
    role: PropTypes.string,
    token: PropTypes.string,
    refreshToken: PropTypes.string,
  }),
  jobOrderId: PropTypes.string,
  onSuccess: PropTypes.func,
};

export default RecommendWorkerCard;
